import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { Waveform } from "../PowerPlays";
import { SERVER_URL } from "../../constants/api";
import "../PowerPlays/PowerPlays.scss";

export default function PowerPlayShare() {
  const { id } = useParams();
  const history = useHistory();
  const [powerPlay, setPowerPlay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPowerPlay = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/public/powerplays/${id}`);
        const data = await res.json();
        if (data.ok && data.result) {
          setPowerPlay(data.result);
        } else {
          setError(true);
        }
      } catch (e) {
        console.error("Error fetching PowerPlay:", e);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPowerPlay();
  }, [id]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const end = new Date(deadline);
    const diff = end - now;
    if (diff < 0) return "Past Deadline";
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} left`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} left`;
    return "Less than 1 hour left";
  };

  if (loading) {
    return (
      <div className="powerplays-page">
        <div className="pp-loading">
          <CircularProgress size={32} sx={{ color: "#E8D44D" }} />
        </div>
      </div>
    );
  }

  if (error || !powerPlay) {
    return (
      <div className="powerplays-page">
        <div className="pp-empty">
          <svg viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          <h3>PowerPlay Not Found</h3>
          <p>This PowerPlay may have been removed or the link is invalid.</p>
          <button
            className="pp-cta"
            style={{ maxWidth: 280, marginTop: 24 }}
            onClick={() => history.push("/")}
          >
            Go to Siki
          </button>
        </div>
      </div>
    );
  }

  const pp = powerPlay;

  return (
    <div className="powerplays-page">
      <div className="pp-cards">
        <div className="pp-card-wrapper">
          <div className="pp-card">
            {/* Hero */}
            <div className="pp-card-hero">
              <div className="pp-scanlines" />

              {pp.photo && (
                <img className="pp-hero-photo" src={pp.photo} alt={pp.title} />
              )}

              <div className="pp-badge">
                <svg viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                <span>PowerPlay</span>
              </div>

              <div className="pp-timer-badge">
                <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" /></svg>
                <span>{getTimeRemaining(pp.deadline)}</span>
              </div>

              <div className="pp-hero-icon">
                <div className="pp-mic-ring">
                  <svg viewBox="0 0 24 24"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" /></svg>
                </div>
                <div className="pp-live-tag">
                  <span className="pp-dot" />
                  Live Stream
                </div>
              </div>

              <Waveform />
            </div>

            {/* Content */}
            <div className="pp-card-content">
              <h2 className="pp-card-title">
                <span className="accent">{pp.title.split(' ')[0]}</span>{' '}
                {pp.title.split(' ').slice(1).join(' ')}
              </h2>

              <p className="pp-card-desc">{pp.description}</p>

              {/* Perks */}
              <div className="pp-perks">
                <div className="pp-perk">
                  <div className="pp-perk-icon yellow">
                    <svg viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                  </div>
                  <div className="pp-perk-text">
                    <span className="pp-perk-label">Reward</span>
                    <span className="pp-perk-value">Featured Artist</span>
                  </div>
                </div>
                <div className="pp-perk">
                  <div className="pp-perk-icon cyan">
                    <svg viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>
                  </div>
                  <div className="pp-perk-text">
                    <span className="pp-perk-label">Access</span>
                    <span className="pp-perk-value">Industry Network</span>
                  </div>
                </div>
              </div>

              {/* Deadline row */}
              <div className="pp-deadline-row">
                <svg viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" /></svg>
                <span className="dl-label">Deadline</span>
                <span className="dl-value">{formatDate(pp.deadline)}</span>
              </div>

              {/* CTA — directs to sign up / app */}
              <button
                className="pp-cta"
                onClick={() => history.push("/app/PowerPlays")}
              >
                View on Siki
              </button>
            </div>

            {/* Footer */}
            <div className="pp-card-footer">
              <span className="pp-footer-tag">Powered by <span className="siki">SIKI</span></span>
              <div className="pp-footer-dots">
                <span /><span /><span />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
